/**
 * Model Avatar Mappings
 *
 * Maps model IDs to avatar image files, and provides avatar resolution logic.
 */

// Avatar mappings - synced with backend constants/llm/avatars.py
// IMPORTANT: Keep this in sync with AI_PROVIDER_AVATARS in the backend
export const MODEL_AVATARS: Record<string, string> = {
  // Anthropic Claude models
  'anthropic/claude-sonnet-4.5': 'anthropic/claude-sonnet-4.5.png',
  'anthropic/claude-sonnet-4': 'anthropic_clauwolf.png',
  'anthropic/claude-opus-4.1': 'anthropic/claude-opus-4.1.png',
  // OpenAI GPT models
  'openai/gpt-5': 'openai/gpt-5.png',
  'openai/gpt-5-pro': 'openai/gpt-5-pro.png',
  'openai/gpt-5-image': 'openai/gpt-5-image.png',
  'openai/gpt-4.1': 'openai/gpt-4.1.png',
  'openai/gpt-4o': 'openai/gpt-4o.png',
  'openai/gpt-4o-mini': 'openai/gpt-4o-mini.png',
  'openai/gpt-4-turbo': 'openai/gpt-4-turbo.png',
  'openai/gpt-4': 'openai/gpt-4.png',
  'openai/gpt-3.5-turbo': 'openai_gepcat.png',
  'openai/o1': 'openai/o1.png',
  'openai/o3': 'openai/o3.png',
  'openai/o3-mini': 'openai/o3-mini.png',
  'openai/o3-pro': 'openai/o3-pro.png',
  'openai/o4-mini': 'openai/o4-mini.png',
  // Google Gemini models
  'google/gemini-2.5-pro': 'google_gemdragon.png',
  'google/gemini-2.5-flash': 'google_gemwolf.png',
  'google/gemini-2.5-flash-image': 'google/gemini-2.5-flash-image.png',
  'google/gemini-2.5-flash-lite': 'google_gemfox.png',
  'google/gemma-3-27b-it': 'google_gembear.png',
  'google/gemma-3-12b-it': 'google_gemhorse.png',
  'google/gemma-3-4b-it': 'google_gemcat.png',
  'google/gemma-4-26b-a4b-it:free': 'google/gemma-4-26b-a4b-it-free.png',
  'google/gemma-4-31b-it:free': 'google/gemma-4-31b-it-free.png',
  // Llama 4 series
  'meta-llama/llama-4-scout': 'meta_llambear.png',
  'meta-llama/llama-4-maverick': 'meta_llamhorse.png',
  // Llama 3.3 series (flagship)
  'meta-llama/llama-3.3-70b-instruct': 'meta-llama/llama-3.3-70b-instruct.png',
  // Llama 3.2 series
  'meta-llama/llama-3.2-3b-instruct': 'meta-llama/llama-3.2-3b-instruct.png',
  'meta-llama/llama-3.2-1b-instruct': 'meta_llamchu.png',
  // Llama 3.1 series
  'meta-llama/llama-3.1-70b-instruct': 'meta_llameagle.png',
  'meta-llama/llama-3.1-8b-instruct': 'meta_llamwolf.png',
  // Llama Guard series (safety models)
  'meta-llama/llama-guard-4-12b': 'meta-llama/llama-guard-4-12b.png',
  // DeepSeek models
  'deepseek/deepseek-r1': 'deepseek/deepseek-r1.png',
  'deepseek/deepseek-r1-0528': 'deepseek/deepseek-r1-0528.png',
  'deepseek/deepseek-chat': 'deepseek_deephorse.png',
  'deepseek/deepseek-chat-v3.1': 'deepseek/deepseek-chat-v3.1.png',
  'deepseek/deepseek-v3.1-terminus': 'deepseek/deepseek-v3.1-terminus.png',
  // Mistral AI models
  'mistralai/mistral-small-3.1-24b-instruct': 'mistralai/mistral-small-3.1-24b-instruct.png',
  'mistralai/mistral-small-3.2-24b-instruct': 'mistralai/mistral-small-3.2-24b-instruct.png',
  'mistralai/mistral-nemo': 'mistralai/mistral-nemo.png',
  // Qwen models (using deepseek avatars as per backend)
  'qwen/qwen3-235b-a22b': 'qwen/qwen3-235b-a22b.png',
  'qwen/qwen3-max': 'qwen/qwen3-max.png',
  'qwen/qwen-plus': 'qwen/qwen-plus.png',
  'qwen/qwen3-coder': 'qwen/qwen3-coder.png',
  'qwen/qwen2.5-vl-72b-instruct': 'qwen/qwen2.5-vl-72b-instruct.png',
  // Cohere models
  'cohere/command-r-plus-08-2024': 'cohere_cohtiger.png',
  'cohere/command-r-08-2024': 'cohere_cohwolf.png',
  'cohere/command-r7b-12-2024': 'cohere_coheagle.png',
  // Nous Research models
  'nousresearch/hermes-3-llama-3.1-405b': 'nous_hermdragon.png',
  // Microsoft Phi models
  'microsoft/phi-4': 'microsoft_phiwolf.png',
  // Amazon Nova models
  'amazon/nova-pro-v1': 'amazon_novadragon.png',
  'amazon/nova-lite-v1': 'amazon_novasquirrel.png',
  'amazon/nova-micro-v1': 'amazon_novarat.png',
  // Nvidia models
  'nvidia/nemotron-3-super-120b-a12b:free': 'nvidia/nemotron-3-super-120b-a12b-free.png',
  // Perplexity models
  'perplexity/sonar': 'perplexity_sonarwolf.png',
  'perplexity/sonar-pro': 'perplexity_sonartiger.png',
  'perplexity/sonar-reasoning-pro': 'perplexity_sonardragon.png',
  'perplexity/sonar-deep-research': 'perplexity_sonareagle.png',
  // Moonshot/Kimi models
  'moonshotai/kimi-k2': 'moonshot_kimitiger.png',
  'moonshotai/kimi-k2-0905': 'moonshot_kimibear.png',
  // Aion Labs
  'aion-labs/aion-rp-llama-3.1-8b': 'openai_gepnix.png',
  // ByteDance
  'bytedance/ui-tars-1.5-7b': 'deepseek_deepbear.png',
  // Cohere extended
  'cohere/command-a': 'cohere_cohrat.png',
  // DeepSeek extended
  'deepseek/deepseek-chat-v3-0324': 'deepseek_deeprat.png',
  'deepseek/deepseek-v3.2-exp': 'deepseek/deepseek-v3.2-exp.png',
  // Google extended
  'google/gemini-2.5-pro-preview': 'google_gemlady.png',
  'google/gemma-2-27b-it': 'google_gemdger.png',
  // Gryphe
  'gryphe/mythomax-l2-13b': 'anthropic_claubear.png',
  // IBM Granite
  'ibm-granite/granite-4.0-h-micro': 'openai_geptiger.png',
  // Mancer
  'mancer/weaver': 'mancer/weaver.png',
  // Microsoft extended
  'microsoft/wizardlm-2-8x22b': 'microsoft_phibit.png',
  // MiniMax
  'minimax/minimax-01': 'minimax/minimax-01.png',
  'minimax/minimax-m1': 'minimax/minimax-m1.png',
  // Mistral extended
  'mistralai/codestral-2508': 'mistral_mistdger.png',
  'mistralai/mistral-large': 'mistral_mistsquirrel.png',
  'mistralai/mistral-large-2407': 'mistralai/mistral-large-2407.png',
  'mistralai/mistral-medium-3': 'mistralai/mistral-medium-3.png',
  'mistralai/mistral-medium-3.1': 'mistralai/mistral-medium-3.1.png',
  'mistralai/mistral-saba': 'mistralai/mistral-saba.png',
  'mistralai/mistral-small-24b-instruct-2501': 'mistralai/mistral-small-24b-instruct-2501.png',
  'mistralai/mixtral-8x22b-instruct': 'mistralai/mixtral-8x22b-instruct.png',
  // Morph
  'morph/morph-v3-fast': 'morph/morph-v3-fast.png',
  'morph/morph-v3-large': 'openai_gepgreat.png',
  // Nous Research extended
  'nousresearch/hermes-3-llama-3.1-70b': 'nous_hermken.png',
  'nousresearch/hermes-4-405b': 'nous_hermcat.png',
  // OpenAI extended
  'openai/gpt-3.5-turbo-0613': 'openai_gepbear.png',
  'openai/gpt-3.5-turbo-16k': 'openai_gephalant.png',
  'openai/gpt-3.5-turbo-instruct': 'openai_gepchilla.png',
  'openai/gpt-4.1-mini': 'openai/gpt-4.1-mini.png',
  'openai/gpt-4.1-nano': 'openai/gpt-4.1-nano.png',
  'openai/gpt-4o-2024-05-13': 'openai_gepcoon.png',
  'openai/gpt-4o-2024-08-06': 'openai_gepfox.png',
  'openai/gpt-4o-2024-11-20': 'openai/gpt-4o-2024-11-20.png',
  'openai/gpt-4o-mini-2024-07-18': 'openai/gpt-4o-mini-2024-07-18.png',
  'openai/gpt-5-image-mini': 'openai/gpt-5-image-mini.png',
  'openai/gpt-5-mini': 'openai/gpt-5-mini.png',
  'openai/gpt-oss-120b': 'openai/gpt-oss-120b.png',
  'openai/gpt-oss-20b': 'openai/gpt-oss-20b.png',
  'openai/o1-pro': 'openai/o1-pro.png',
  'openai/o3-mini-high': 'openai/o3-mini-high.png',
  'openai/o4-mini-high': 'openai/o4-mini-high.png',
  // OpenRouter
  'openrouter/auto': 'openrouter/auto.png',
  // Qwen extended
  'qwen/qwen-2.5-72b-instruct': 'qwen/qwen-2.5-72b-instruct.png',
  'qwen/qwen-2.5-7b-instruct': 'qwen/qwen-2.5-7b-instruct.png',
  'qwen/qwen-2.5-coder-32b-instruct': 'qwen/qwen-2.5-coder-32b-instruct.png',
  'qwen/qwen-plus-2025-07-28': 'qwen/qwen-plus-2025-07-28.png',
  'qwen/qwen3-14b': 'qwen/qwen3-14b.png',
  'qwen/qwen3-235b-a22b-2507': 'qwen/qwen3-235b-a22b-2507.png',
  'qwen/qwen3-235b-a22b-thinking-2507': 'qwen/qwen3-235b-a22b-thinking-2507.png',
  'qwen/qwen3-30b-a3b': 'qwen/qwen3-30b-a3b.png',
  'qwen/qwen3-30b-a3b-instruct-2507': 'qwen/qwen3-30b-a3b-instruct-2507.png',
  'qwen/qwen3-30b-a3b-thinking-2507': 'qwen/qwen3-30b-a3b-thinking-2507.png',
  'qwen/qwen3-32b': 'qwen/qwen3-32b.png',
  'qwen/qwen3-8b': 'qwen/qwen3-8b.png',
  'qwen/qwen3-coder-30b-a3b-instruct': 'qwen/qwen3-coder-30b-a3b-instruct.png',
  'qwen/qwen3-coder-flash': 'qwen/qwen3-coder-flash.png',
  'qwen/qwen3-coder-plus': 'qwen/qwen3-coder-plus.png',
  'qwen/qwen3-next-80b-a3b-instruct': 'qwen/qwen3-next-80b-a3b-instruct.png',
  'qwen/qwen3-next-80b-a3b-thinking': 'qwen/qwen3-next-80b-a3b-thinking.png',
  'qwen/qwen3-vl-235b-a22b-instruct': 'qwen/qwen3-vl-235b-a22b-instruct.png',
  'qwen/qwen3-vl-235b-a22b-thinking': 'qwen/qwen3-vl-235b-a22b-thinking.png',
  'qwen/qwen3-vl-30b-a3b-instruct': 'qwen/qwen3-vl-30b-a3b-instruct.png',
  'qwen/qwen3-vl-30b-a3b-thinking': 'qwen/qwen3-vl-30b-a3b-thinking.png',
  'qwen/qwen3-vl-32b-instruct': 'qwen/qwen3-vl-32b-instruct.png',
  'qwen/qwen3-vl-8b-instruct': 'qwen/qwen3-vl-8b-instruct.png',
  'qwen/qwen3-vl-8b-thinking': 'qwen/qwen3-vl-8b-thinking.png',
  // Relace
  'relace/relace-apply-3': 'relace/relace-apply-3.png',
  // SAO10K
  'sao10k/l3-lunaris-8b': 'sao10k/l3-lunaris-8b.png',
  'sao10k/l3.1-euryale-70b': 'sao10k/l3.1-euryale-70b.png',
  'sao10k/l3.3-euryale-70b': 'sao10k/l3.3-euryale-70b.png',
  // Tencent
  'tencent/hunyuan-a13b-instruct': 'tencent/hunyuan-a13b-instruct.png',
  // TheDrummer
  'thedrummer/cydonia-24b-v4.1': 'thedrummer/cydonia-24b-v4.1.png',
  'thedrummer/skyfall-36b-v2': 'thedrummer/skyfall-36b-v2.png',
  'thedrummer/unslopnemo-12b': 'thedrummer/unslopnemo-12b.png',
  // Undi95
  'undi95/remm-slerp-l2-13b': 'undi95/remm-slerp-l2-13b.png',
  // Z-AI (GLM)
  'z-ai/glm-4.5': 'z-ai/glm-4.5.png',
  'z-ai/glm-4.5-air': 'z-ai/glm-4.5-air.png',
  'z-ai/glm-4.5v': 'z-ai/glm-4.5v.png',
  'z-ai/glm-4.6': 'z-ai/glm-4.6.png',
  // Anthracite
  'anthracite-org/magnum-v4-72b': 'anthropic_claunix.png',
  // Anthropic extended
  'anthropic/claude-haiku-4.5': 'anthropic/claude-haiku-4.5.png',
};

// Available avatars per provider (all exist in GCS)
// Used for automatic distribution when model has no specific mapping
export const PROVIDER_AVATARS: Record<string, string[]> = {
  anthropic: [
    'anthropic_claudragon.png',
    'anthropic_claunix.png',
    'anthropic_clauken.png',
    'anthropic_clauphant.png',
    'anthropic_clausaber.png',
    'anthropic_clauwolf.png',
    'anthropic_claugle.png',
    'anthropic_claufox.png',
    'anthropic_claucoon.png',
    'anthropic_claucat.png',
    'anthropic_clauhorse.png',
    'anthropic_claubear.png',
  ],
  openai: [
    'openai_gepthor.png',
    'openai_gephydra.png',
    'openai_gepnix.png',
    'openai_gepken.png',
    'openai_gepeagle.png',
    'openai_geptiger.png',
    'openai_gepwolf.png',
    'openai_gepsaber.png',
    'openai_gepotter.png',
    'openai_gepgreat.png',
    'openai_gepcoon.png',
    'openai_gepfox.png',
    'openai_gepcat.png',
    'openai_gephalant.png',
    'openai_gepchilla.png',
    'openai_geptiger.png',
    'openai_gepfalcon.png',
    'openai_gepven.png',
    'openai_gephorse.png',
    'openai_gepbear.png',
  ],
  google: [
    'google_gemdragon.png',
    'google_gemwolf.png',
    'google_gemeagle.png',
    'google_gemtiger.png',
    'google_gembear.png',
    'google_gemhorse.png',
    'google_gemcat.png',
    'google_gemfox.png',
    'google_gemotter.png',
    'google_gemphin.png',
    'google_gemant.png',
    'google_gembee.png',
    'google_gemcoon.png',
    'google_gemrilla.png',
    'google_gemrat.png',
    'google_gemsquirrel.png',
  ],
  'meta-llama': [
    'meta_llamdragon.png',
    'meta_llamnix.png',
    'meta_llameagle.png',
    'meta_llamtiger.png',
    'meta_llamwolf.png',
    'meta_llambear.png',
    'meta_llamhorse.png',
    'meta_llambit.png',
    'meta_llamchu.png',
    'meta_llamphin.png',
    'meta_llamrat.png',
    'meta_llamsquirrel.png',
  ],
  deepseek: [
    'deepseek_deepdragon.png',
    'deepseek_deepeagle.png',
    'deepseek_deeptiger.png',
    'deepseek_deepwolf.png',
    'deepseek_deepbear.png',
    'deepseek_deephorse.png',
    'deepseek_deepphin.png',
    'deepseek_deepcat.png',
    'deepseek_deeprilla.png',
    'deepseek_deepelk.png',
    'deepseek_deeprat.png',
    'deepseek_deepsquirrel.png',
  ],
  mistralai: [
    'mistral_mistdire.png',
    'mistral_misteagle.png',
    'mistral_misttiger.png',
    'mistral_mistwolf.png',
    'mistral_mistbear.png',
    'mistral_misthorse.png',
    'mistral_mistfox.png',
    'mistral_mistotter.png',
    'mistral_mistcoon.png',
    'mistral_mistgreat.png',
    'mistral_mistsaber.png',
    'mistral_mistphin.png',
    'mistral_mistcat.png',
    'mistral_mistchu.png',
    'mistral_mistbit.png',
  ],
  'x-ai': [
    'xai_grokdragon.png',
    'xai_grokeagle.png',
    'xai_groktiger.png',
    'xai_grokwolf.png',
    'xai_grokbear.png',
    'xai_grokhorse.png',
    'xai_grokfox.png',
    'xai_grokcat.png',
  ],
  qwen: [
    'deepseek_deepdragon.png',
    'deepseek_deeptiger.png',
    'deepseek_deepeagle.png',
    'deepseek_deepwolf.png',
    'deepseek_deepphin.png',
    'deepseek_deeprilla.png',
    'deepseek_deepbear.png',
    'deepseek_deepcat.png',
  ],
  cohere: ['cohere_cohtiger.png', 'cohere_cohwolf.png', 'cohere_coheagle.png', 'cohere_cohrat.png'],
  nousresearch: [
    'nous_hermdragon.png',
    'nous_hermnix.png',
    'nous_hermken.png',
    'nous_hermtiger.png',
    'nous_hermwolf.png',
    'nous_hermcat.png',
    'nous_hermrat.png',
    'nous_hermsquirrel.png',
  ],
  microsoft: [
    'microsoft_phiwolf.png',
    'microsoft_phifox.png',
    'microsoft_phicoon.png',
    'microsoft_phicat.png',
    'microsoft_phibit.png',
    'microsoft_phichu.png',
    'microsoft_phirat.png',
    'microsoft_phisquirrel.png',
  ],
  nvidia: [
    'nvidia_nemodire.png',
    'nvidia_nemowolf.png',
    'nvidia_nemochu.png',
    'nvidia_nemorat.png',
    'nvidia_nemosquirrel.png',
  ],
  perplexity: [
    'perplexity_sonardragon.png',
    'perplexity_sonarnix.png',
    'perplexity_sonartiger.png',
    'perplexity_sonarwolf.png',
    'perplexity_sonareagle.png',
  ],
  moonshotai: [
    'moonshot_kimitiger.png',
    'moonshot_kimiwolf.png',
    'moonshot_kimibear.png',
    'moonshot_kimieagle.png',
    'moonshot_kimihorse.png',
  ],
  amazon: ['amazon_novadragon.png', 'amazon_novasquirrel.png', 'amazon_novarat.png'],
  ai21: ['ai21_jambadire.png', 'ai21_jambacat.png'],
};

// Models that have specific subdirectory avatars in GCS (generated today)
// These take priority over hash-based selection
// Format: 'provider/model-name:suffix' -> will be converted to 'provider/model-name-suffix.png'
export const SUBDIRECTORY_AVATAR_MODELS = new Set([
  'allenai/molmo-2-8b:free',
  'arcee-ai/trinity-large-preview:free',
  'arcee-ai/trinity-mini:free',
  'cognitivecomputations/dolphin-mistral-24b-venice-edition:free',
  'cohere/north-mini-code:free',
  'deepseek/deepseek-r1-0528:free',
  'google/gemma-3-12b-it:free',
  'google/gemma-3-27b-it:free',
  'google/gemma-3-4b-it:free',
  'google/gemma-3n-e2b-it:free',
  'google/gemma-3n-e4b-it:free',
  'google/gemma-4-26b-a4b-it:free',
  'google/gemma-4-31b-it:free',
  'inclusionai/ling-3.0-flash:free',
  'inclusionai/ling-3.0-tiny:free',
  'liquid/lfm-2.5-1.2b-instruct:free',
  'liquid/lfm-2.5-1.2b-thinking:free',
  'liquid/lfm-2.5-2.6b:free',
  'meta-llama/llama-3.1-405b-instruct:free',
  'meta-llama/llama-3.2-3b-instruct:free',
  'meta-llama/llama-3.3-70b-instruct:free',
  'mistralai/mistral-small-3.1-24b-instruct:free',
  'nousresearch/hermes-3-llama-3.1-405b:free',
  'nvidia/nemotron-3-nano-30b-a3b:free',
  'nvidia/nemotron-3-super-120b-a12b:free',
  'nvidia/nemotron-3-ultra-550b-a55b:free',
  'nvidia/nemotron-3.5-content-safety:free',
  'nvidia/nemotron-3.5-lightning:free',
  'nvidia/nemotron-nano-12b-v2-vl:free',
  'nvidia/nemotron-nano-9b-v2:free',
  'openai/gpt-oss-120b:free',
  'openai/gpt-oss-20b:free',
  'openrouter/free',
  'poolside/laguna-xs-2.1:free',
  'poolside/laguna-s-2.1:free',
  'qwen/qwen-2.5-coder-32b-instruct',
  'qwen/qwen-2.5-vl-7b-instruct:free',
  'qwen/qwen3-4b:free',
  'qwen/qwen3-coder:free',
  'qwen/qwen3-next-80b-a3b-instruct:free',
  'stepfun/step-3.5-flash:free',
  'tencent/hy3:free',
  'tngtech/deepseek-r1t-chimera:free',
  'tngtech/deepseek-r1t2-chimera:free',
  'tngtech/tng-r1t-chimera:free',
  'upstage/solar-pro-3:free',
  'z-ai/glm-4.5-air:free',
]);

// Convert model ID to subdirectory avatar path
// meta-llama/llama-3.3-70b-instruct:free -> meta-llama/llama-3.3-70b-instruct-free.png
export function getSubdirectoryAvatarPath(modelId: string): string {
  return modelId.replace(/:free/g, '-free').replace(/:thinking/g, '-thinking') + '.png';
}

// Simple hash function for consistent avatar selection
export function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

export function getAvatarForModel(modelId: string, allowSubdirectory = true): string {
  // 1. Check if model has a specific subdirectory avatar (highest priority)
  // These are model-specific avatars generated and stored in GCS subdirectories.
  // In local/Electron mode, subdirectory avatars don't exist on the filesystem,
  // so skip them and fall through to hash-based selection with flat filenames.
  if (allowSubdirectory && SUBDIRECTORY_AVATAR_MODELS.has(modelId)) {
    return getSubdirectoryAvatarPath(modelId);
  }

  // 2. Check for manually assigned avatar in MODEL_AVATARS
  if (MODEL_AVATARS[modelId]) {
    const avatar = MODEL_AVATARS[modelId];
    if (allowSubdirectory || !avatar.includes('/')) return avatar;
  }

  // 3. Also check without :free/:exacto/:thinking suffix
  const baseModelId = modelId.replace(/:free|:exacto|:thinking/g, '');
  if (MODEL_AVATARS[baseModelId]) {
    const avatar = MODEL_AVATARS[baseModelId];
    if (allowSubdirectory || !avatar.includes('/')) return avatar;
  }

  // 4. Get provider and available avatars for hash-based selection
  const provider = modelId.split('/')[0];
  const avatars = PROVIDER_AVATARS[provider];

  // If provider has avatars, select one based on model name hash
  if (avatars && avatars.length > 0) {
    const hash = hashString(baseModelId);
    const index = hash % avatars.length;
    return avatars[index];
  }

  // 5. Fallback: use generic avatars for unknown providers
  const genericAvatars = PROVIDER_AVATARS['openai'];
  const hash = hashString(modelId);
  return genericAvatars[hash % genericAvatars.length];
}

export function getModelDisplayName(modelId: string): string {
  const parts = modelId.split('/');
  if (parts.length < 2) return modelId;
  let modelName = parts[1];
  modelName = modelName.replace(':free', '').replace(':thinking', ' (Thinking)');
  return modelName
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
